import { NonCachedSearchType } from 'interfaces'
import { FilterGroupName } from 'results/constants'
import {
  DEFAULT_FILTER_SECTION_ORDER,
  FILTER_GROUP_SECTION_MAP,
  FILTER_SECTION_ORDER_BY_SEARCH_TYPE,
  FilterSectionId,
  PINNED_FIRST_FILTER_GROUPS,
  VEHICLE_FITMENT_SUB_ORDER,
} from './constants'

export type SpecialFilterSection = 'GFX'

export interface FilterGroupModel {
  groupName: string
  items: string[]
}

export interface OrderedFilterEntry {
  sectionId: FilterSectionId
  groupName: string
  filter?: FilterGroupModel
}

export const getFilterSectionOrder = (
  searchType?: NonCachedSearchType
): FilterSectionId[] => {
  if (searchType && FILTER_SECTION_ORDER_BY_SEARCH_TYPE[searchType]) {
    return FILTER_SECTION_ORDER_BY_SEARCH_TYPE[searchType]
  }
  return DEFAULT_FILTER_SECTION_ORDER
}

export const getSectionSortIndex = (
  sectionId: FilterSectionId,
  searchType?: NonCachedSearchType
): number => {
  const order = getFilterSectionOrder(searchType)
  const index = order.indexOf(sectionId)
  if (index === -1) {
    return order.length
  }
  return index
}

export const getFilterSectionId = (groupName: string): FilterSectionId =>
  FILTER_GROUP_SECTION_MAP[groupName] ?? FilterSectionId.ATTRIBUTES

const getPinnedGroupSortIndex = (groupName: string): number | null => {
  const pinnedIndex = PINNED_FIRST_FILTER_GROUPS.indexOf(
    groupName as FilterGroupName
  )
  if (pinnedIndex === -1) {
    return null
  }
  return pinnedIndex
}

const getGroupSortIndex = (
  groupName: string,
  searchType?: NonCachedSearchType
): number => {
  const pinnedIndex = getPinnedGroupSortIndex(groupName)
  if (pinnedIndex !== null) {
    return pinnedIndex
  }
  const sectionId = getFilterSectionId(groupName)
  return (
    PINNED_FIRST_FILTER_GROUPS.length +
    getSectionSortIndex(sectionId, searchType)
  )
}

export const sortFiltersBySectionOrder = <T extends FilterGroupModel>(
  filters: T[],
  searchType?: NonCachedSearchType
): T[] =>
  filters.slice().sort((a, b) => {
    const orderDiff =
      getGroupSortIndex(a.groupName, searchType) -
      getGroupSortIndex(b.groupName, searchType)
    if (orderDiff !== 0) {
      return orderDiff
    }
    const subA = VEHICLE_FITMENT_SUB_ORDER[a.groupName] ?? 0
    const subB = VEHICLE_FITMENT_SUB_ORDER[b.groupName] ?? 0
    return subA - subB
  })

export const buildRenderableFilterSections = (options: {
  filters: FilterGroupModel[]
  hasGfx: boolean
  searchType?: NonCachedSearchType
}): OrderedFilterEntry[] => {
  const { filters, hasGfx, searchType } = options
  const entries: OrderedFilterEntry[] = []

  if (hasGfx) {
    entries.push({
      sectionId: FilterSectionId.VEHICLE_FITMENT,
      groupName: 'GFX',
    })
  }

  sortFiltersBySectionOrder(filters, searchType).forEach((filter) => {
    if (filter.items.length < 1) {
      return
    }
    entries.push({
      sectionId: getFilterSectionId(filter.groupName),
      groupName: filter.groupName,
      filter,
    })
  })

  return entries.sort((a, b) => {
    const orderDiff =
      getGroupSortIndex(a.groupName, searchType) -
      getGroupSortIndex(b.groupName, searchType)
    if (orderDiff !== 0) {
      return orderDiff
    }
    const subA = VEHICLE_FITMENT_SUB_ORDER[a.groupName] ?? 99
    const subB = VEHICLE_FITMENT_SUB_ORDER[b.groupName] ?? 99
    return subA - subB
  })
}
